import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCopy } from '../../context/CopyContext';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import PoweredBy from '../PoweredBy';
import CarouselSnap from '../CarouselSnap';

const BeforeStartView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();

  const handleBack = () => {
    dispatch({ type: 'SET_STEP', payload: 1 });
    navigate('/');
  };

  const handleContinue = () => {
    dispatch({ type: 'SET_STEP', payload: 3 });
    navigate('/add-empty');
  };


  // Carousel items with actual images
  const carouselItems = [
    {
      id: 'small-format',
      image: '/small-format-cosmetics.jpg',
      title: 'Small format beauty packaging'
    },
    {
      id: 'clean-empty',
      image: '/clean-and-empty.png',
      title: 'Clean and empty'
    },
    {
      id: 'non-hazardous',
      image: '/non-hazardous.png',
      title: 'Non-hazardous'
    }
  ];


  return (
    <div className="bg-brand-background min-h-screen flex flex-col" data-node-id="61:309">
      {/* Container with flexbox structure - no padding here */}
      <div className="flex-1 flex flex-col">
        
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header 
              showItems={false} 
              itemsNumber={state.itemsCount.toString()}
            />
          </div>
        </div>

        {/* Main Content Area - Animated */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading and subtitle with padding */}
          <div className="flex items-center justify-center mx-20">
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('beforeStart.heading')}
                  subtitle={getCopy('beforeStart.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Rows 2 & 3 - List spanning two rows - FULL WIDTH */}
          <div className="row-span-2 flex items-center justify-center overflow-x-auto pb-24">
            <div className="w-full h-full max-w-[680px]">
              <div className="flex flex-col gap-10 items-start w-full">
                {carouselItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="border border-brand-border border-solid box-border flex gap-10 items-center p-6 rounded-3xl w-full bg-brand-background"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: 0.75 + (0.1 * index),
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                  >
                    <div className="relative rounded-2xl shrink-0 w-20 h-20">
                      <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
                        <img 
                          alt={item.title}
                          className="absolute h-full w-full object-cover"
                          src={item.image}
                        />
                      </div>
                    </div>
                    <p className="font-medium leading-9 relative shrink-0 text-3xl text-brand-text-primary text-center text-nowrap whitespace-pre">
                      {item.title}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-6 lg:gap-10 w-full">
            <Button 
              variant="secondary" 
              onClick={handleBack}
            >
              {getCopy('beforeStart.buttons.secondary')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleContinue}
            >
              Confirm
            </Button>
          </div>



          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeStartView;
